import { PropsWithChildren, ReactElement, useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { Box, Slide, useScrollTrigger } from '@mui/material';

import { NotificationBannerContentDisplay, NotificationBannerContentDisplayProps } from '../NotificationBanner';
import { NavigationBarProps, NavigationBar } from './NavigationBar';
import { NavigationBarContext, navigationBarTransition } from './NavigationBarContext';

import { useIsMobile } from '../hooks';

export interface NavigationBarDisplayProps<T> {
  NavigationBarProps: Omit<NavigationBarProps<T>, 'children'> & { isTopNavOnPage?: boolean };
  NotificationBannerContentDisplayProps?: Omit<NotificationBannerContentDisplayProps, 'children'> & {
    isNotificationBannerOnPage?: boolean;
  };
}

const DEFAULT_NAV_HEIGHT = 64;

export const NavigationBarDisplay = <T extends ReactElement>({
  children,
  NavigationBarProps: { isTopNavOnPage, ...DesktopTopNavProps },
  NotificationBannerContentDisplayProps,
}: PropsWithChildren<NavigationBarDisplayProps<T>>) => {
  const [isFixed, setIsFixed] = useState(false);

  const isMobile = useIsMobile();

  const trigger = useScrollTrigger({ threshold: 600 });
  const { ref: navRef, height: foundNavHeight } = useResizeDetector({ handleHeight: true, handleWidth: true });
  const navHeight = useMemo(() => foundNavHeight ?? DEFAULT_NAV_HEIGHT, [foundNavHeight]);

  const navIn = useMemo(
    () => isTopNavOnPage && (!isMobile || isFixed || !trigger),
    [isTopNavOnPage, isFixed, trigger, isMobile]
  );

  const { isNotificationBannerOnPage, ...NotificationBannerContentDisplayPropsRest } =
    NotificationBannerContentDisplayProps || {};
  const { ref: notificationRef, height: notificationHeight } = useResizeDetector({
    handleHeight: true,
    handleWidth: true,
  });

  const contextValue = useMemo(
    () => ({
      setIsFixed,
      offset:
        ((navIn ? navHeight : undefined) ?? 0) + ((isNotificationBannerOnPage ? notificationHeight : undefined) ?? 0),
      transition: navigationBarTransition,
    }),
    [navIn, navHeight, isNotificationBannerOnPage, notificationHeight]
  );

  return (
    <NavigationBarContext.Provider value={contextValue}>
      <Slide appear={false} direction="down" in={navIn}>
        <NavigationBar ref={navRef} {...DesktopTopNavProps} />
      </Slide>
      <Box height={isTopNavOnPage ? navHeight : 0} />
      {NotificationBannerContentDisplayProps && (
        <>
          <Slide direction="down" in={isNotificationBannerOnPage}>
            <Box
              ref={notificationRef}
              position="fixed"
              width="100%"
              zIndex={({ zIndex }) => zIndex.appBar - 1}
              style={{
                transition: `transform ${contextValue.transition}`,
                transform: `translateY(${navIn ? 0 : -(navHeight ?? 0)}px)`,
              }}
            >
              <NotificationBannerContentDisplay {...NotificationBannerContentDisplayPropsRest} />
            </Box>
          </Slide>
          <Box height={isNotificationBannerOnPage ? notificationHeight : 0} />
        </>
      )}
      {children}
    </NavigationBarContext.Provider>
  );
};
